// An on-screen table (vue-good-table) as a file that Excel opens right, in Hebrew.
//
// CSV: Excel reads a CSV in the computer's own code page unless the file starts with a UTF-8 BOM. Without it
// every Hebrew letter came out as ׳ and a symbol ("׳₪׳ ׳™׳•׳×" for פניות, "◊§◊†◊ô◊ï◊™" on a Mac), while Google
// Sheets / Numbers guess UTF-8 by themselves, so the same file looked fine there (2/11/2026, a business owner
// on the פניות export: "הכל יוצא מלל הפוך"). Every CSV goes out with the BOM, and a cell holding a comma, a
// quote or a line break is quoted (RFC 4180) instead of losing its commas.
//
// XLSX: SheetJS (window.XLSX, /static/js/sheet.js) reads the table. The sheet is right-to-left like the screen
// (without it Excel opens it left-to-right, the first column on the far left), named after the list, its
// columns sized to their text, a phone number kept as text (0501234567, not 501234567) and the template's
// stray whitespace trimmed.

export const CSV_BOM = '﻿';

const SHEET_JS_URL = '/static/js/sheet.js';

// a phone number: leading 0 or +, then 7+ digits / dashes / spaces. SheetJS would make it a number and drop
// the 0; a number like that is never a quantity
const PHONE_LIKE = /^(0\d[\d-]{6,}|\+\d[\d -]{6,})$/;

// one cell's text for a CSV: one line, no ₪, no padding; a number loses its thousands separators so it reads
// as a number in every locale (12,500 → 12500)
export function csvCellText(text) {
    let value = String(text == null ? '' : text)
        .replace(/₪/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(value))
        value = value.replace(/,/g, '');
    return value;
}

// RFC 4180: quoted only when it has to be, its quotes doubled (בע"מ → "בע""מ")
export function csvField(value) {
    const text = value == null ? '' : String(value);
    return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}

// the rows of a table as CSV text: every <tr>, its th / td cells as the screen shows them (innerText)
export function tableToCsv(tableElement) {
    const lines = [];
    const rows = tableElement.querySelectorAll('tr');
    for (let i = 0; i < rows.length; i++) {
        const cells = rows[i].querySelectorAll('td, th');
        const fields = [];
        for (let j = 0; j < cells.length; j++) {
            let text = cells[j].innerText;
            // a sortable vue-good-table header carries a screen-reader text: "Sort table by ..."
            const sortHint = text.indexOf('Sort table');
            if (sortHint != -1)
                text = text.substring(0, sortHint);
            fields.push(csvField(csvCellText(text)));
        }
        lines.push(fields.join(','));
    }
    return lines.join('\n');
}

export function downloadCsv(csvText, fileName) {
    const blob = new Blob([CSV_BOM + csvText], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.download = fileName;
    link.href = window.URL.createObjectURL(blob);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        if (link.parentNode)
            link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    }, 60000);
}

// window.XLSX, fetched once per page load (~1.3MB). Resolves with it; a failed load can be tried again
let sheetJsLoading = null;

export function sheetJsReady() {
    if (window.XLSX && window.XLSX.utils && window.XLSX.writeFile)
        return Promise.resolve(window.XLSX);
    if (!sheetJsLoading) {
        sheetJsLoading = fetch(SHEET_JS_URL)
            .then(response => {
                if (!response.ok)
                    throw new Error('sheet.js: HTTP ' + response.status);
                return response.text();
            })
            .then(code => {
                const script = document.createElement('script');
                script.textContent = code;
                document.body.appendChild(script);
                if (!window.XLSX || !window.XLSX.utils)
                    throw new Error('sheet.js did not define XLSX');
                return window.XLSX;
            })
            .catch(error => {
                sheetJsLoading = null;
                throw error;
            });
    }
    return sheetJsLoading;
}

// Excel: at most 31 characters, none of \ / ? * [ ] :
function sheetTitle(name) {
    const title = String(name || '').replace(/[\\\/?*[\]:]/g, ' ').trim().slice(0, 31);
    return title || 'Sheet1';
}

function longestLine(text) {
    const lines = String(text).split('\n');
    let longest = 0;
    for (let i = 0; i < lines.length; i++)
        longest = Math.max(longest, lines[i].length);
    return longest;
}

// string cells trimmed (the templates' indentation and &nbsp;s), every column as wide as its longest text
function tidySheet(XLSX, sheet) {
    if (!sheet['!ref'])
        return;
    const range = XLSX.utils.decode_range(sheet['!ref']);
    const widths = [];
    for (let r = range.s.r; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r: r, c: c })];
            if (!cell)
                continue;
            let length = 0;
            if (cell.t == 's' && typeof cell.v == 'string') {
                cell.v = cell.v.replace(/[^\S\n]+/g, ' ').replace(/ ?\n ?/g, '\n').trim();
                cell.w = cell.v;
                length = longestLine(cell.v);
            } else if (cell.t == 'n') {
                // a date / time (it has a number format) shows as about ten characters
                length = cell.z ? 10 : String(cell.v).length;
            } else if (cell.t == 'b' || cell.t == 'd') {
                length = 10;
            }
            widths[c] = Math.max(widths[c] || 0, length);
        }
    }
    const cols = [];
    for (let c = 0; c <= range.e.c; c++)
        cols.push({ wch: Math.min(Math.max((widths[c] || 0) + 2, 8), 60) });
    sheet['!cols'] = cols;
}

// the table as <fileName>.xlsx, one right-to-left sheet. Needs window.XLSX (sheetJsReady)
export function exportTableToXlsx(tableElement, fileName, sheetName) {
    const XLSX = window.XLSX;
    // SheetJS reads data-t off the cell itself: "s" keeps the text as it is
    const cells = tableElement.querySelectorAll('td, th');
    for (let i = 0; i < cells.length; i++) {
        if (PHONE_LIKE.test(cells[i].textContent.replace(/\s+/g, ' ').trim()))
            cells[i].setAttribute('data-t', 's');
    }
    const book = XLSX.utils.table_to_book(tableElement, { sheet: sheetTitle(sheetName) });
    tidySheet(XLSX, book.Sheets[book.SheetNames[0]]);
    book.Workbook = { Views: [{ RTL: true }] };
    XLSX.writeFile(book, fileName);
}
